<template>
  <div v-if="proposal" class="color-contrast-proposal" role="status">
    <p class="color-contrast-proposal__warning">
      {{ t('admin.settings.form.colorContrast.warning', { ratio: formattedRatio }) }}
    </p>
    <div class="color-contrast-proposal__row">
      <span
        class="color-contrast-proposal__swatch"
        :style="{ backgroundColor: proposal }"
        :title="proposal"
        aria-hidden="true"
      />
      <span class="color-contrast-proposal__hex">{{ proposal }}</span>
      <CustomButton
        usage="button"
        color="white"
        buttonType="button"
        :text="t('admin.settings.form.colorContrast.accept')"
        testId="color-contrast-accept"
        @emit:click="acceptSuggestion"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import CustomButton from '@/features/ui/components/button/custom-button.vue'
import { useI18n } from '@/lib/i18n'
import { meetsTextContrast, suggestTextContrast, textContrastRatio } from '@/utils/misc/contrast'

const props = defineProps<{
  hex?: string | null
}>()

const emit = defineEmits<{
  accept: [color: string]
}>()

const { t, locale } = useI18n()

const proposal = computed(() => {
  const value = props.hex?.trim()
  if (!value || meetsTextContrast(value)) return null
  return suggestTextContrast(value)
})

function acceptSuggestion() {
  if (proposal.value) emit('accept', proposal.value)
}

const formattedRatio = computed(() => {
  const value = props.hex?.trim()
  if (!value) return ''
  const ratio = textContrastRatio(value)
  if (ratio === null) return ''
  return new Intl.NumberFormat(locale.value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(ratio)
})
</script>

<style scoped lang="scss">
.color-contrast-proposal {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.75rem 0.875rem;
  border: 1px solid rgb(var(--color-badge-warning-fg));
  border-radius: 8px;
  background-color: rgb(var(--color-badge-warning-bg));
}

.color-contrast-proposal__warning {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: rgb(var(--color-badge-warning-fg));
}

.color-contrast-proposal__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.color-contrast-proposal__swatch {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 4px;
  border: 1px solid rgba(27, 27, 30, 0.15);
  flex-shrink: 0;
}

.color-contrast-proposal__hex {
  font-family: ui-monospace, monospace;
  font-size: 0.8125rem;
  color: rgb(var(--rgb-black));
}
</style>
